import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Persistent "Payment options" selector shared by the four payment screens
 * (issue #116).
 *
 * The Payments area splits across four routes — Transfer between accounts
 * (`/funds-transfer`), Bill Payments (`/payments`), Standing orders
 * (`/standing-orders`) and Direct Debits (`/direct-debits`). Previously, picking
 * any of the three non-Bill-Payments options from the `/payments` dropdown
 * navigated straight to its own screen, leaving the agent with no way to switch
 * to another option without going back to the dashboard and re-entering
 * Payments.
 *
 * This component keeps the option selector visible on every payment screen so
 * the agent can switch directly between the four options (e.g. funds transfer ↔
 * direct debits) in one click. It is a pure navigation chooser — each
 * destination route stays independently RBAC-gated (deny-by-default), so a menu
 * entry can never bypass a screen's permission gate. It mirrors the accessible
 * listbox dropdown used elsewhere on the payment screens.
 */

/** The canonical four payment options, in the customer's Figma order, each
 * mapped to its route. Single source of truth so the selector stays consistent
 * across screens. */
export const PAYMENT_OPTIONS = [
  { value: 'Transfer between accounts', route: '/funds-transfer' },
  { value: 'Bill Payments', route: '/payments' },
  { value: 'Standing orders', route: '/standing-orders' },
  { value: 'Direct Debits', route: '/direct-debits' },
] as const;

export type PaymentOptionValue = (typeof PAYMENT_OPTIONS)[number]['value'];

export function PaymentOptionsNav({
  current,
}: {
  /** The payment option the current screen represents (highlighted + a no-op
   * when re-selected). */
  current: PaymentOptionValue;
}): React.JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  function choose(option: (typeof PAYMENT_OPTIONS)[number]): void {
    setOpen(false);
    // Re-selecting the current option is a no-op (already on that screen).
    // Carry the chosen option as navigation state so the destination can open
    // with that option already selected — selecting an option here IS the agent
    // actively choosing it. Bill Payments (`/payments`) consumes this to
    // pre-select the option on arrival (issue #501, Option A), removing the
    // double-selection the tester hit; a fresh arrival (dashboard/process menu)
    // carries no such state, so no option is pre-selected (issue #364 intact).
    if (option.value !== current)
      navigate(option.route, { state: { paymentOption: option.value } });
  }

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor="payment-options-nav"
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        Payment options
      </label>
      <div className="relative w-[260px]">
        <button
          id="payment-options-nav"
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span>{current}</span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label="Payment options"
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {PAYMENT_OPTIONS.map((option) => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === current}
                tabIndex={0}
                onClick={() => choose(option)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    choose(option);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  option.value === current ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.value}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
